import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  BanknotesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  PlusCircleIcon,
  QrCodeIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { paymentGatewayAPI, type PaymentGateway } from '@/services/api'
import CreatePixOrderModal from '@/components/integrations/CreatePixOrderModal'
import CreateCreditCardOrderModal from '@/components/integrations/CreateCreditCardOrderModal'
import CreateBoletoOrderModal from '@/components/integrations/CreateBoletoOrderModal'

interface Transaction {
  id: string
  date: string
  description: string
  amount: number
  type: 'RECEIVED' | 'PAYMENT' | 'TRANSFER'
  status: string
  customer?: string | { name?: string; email?: string; [key: string]: any }
}

const PagarmeDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [gateway, setGateway] = useState<PaymentGateway | null>(null)
  const [balance, setBalance] = useState<any>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncingFullHistory, setSyncingFullHistory] = useState(false)
  const [syncingToBank, setSyncingToBank] = useState(false)
  const [showPixModal, setShowPixModal] = useState(false)
  const [showCardModal, setShowCardModal] = useState(false)
  const [showBoletoModal, setShowBoletoModal] = useState(false)
  const [showCreateMenu, setShowCreateMenu] = useState(false)

  // Filtros e paginação
  const [periodFilter, setPeriodFilter] = useState(15)  // Padrão: 15 dias
  const [periodLabel, setPeriodLabel] = useState('Últimos 15 dias')
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false)
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  useEffect(() => {
    if (id) {
      loadGatewayData()
    }
  }, [id, periodFilter])  // Recarregar quando filtro mudar

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.period-filter-dropdown')) {
        setShowPeriodDropdown(false)
        setShowCustomDatePicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadGatewayData = async (days?: number) => {
    const daysToFetch = days !== undefined ? days : periodFilter
    try {
      setLoading(true)
      const [gatewayData, balanceData, transactionsData] = await Promise.all([
        paymentGatewayAPI.getGateway(id!),
        paymentGatewayAPI.getGatewayBalance(id!).catch(() => null),
        paymentGatewayAPI.getGatewayTransactions(id!, daysToFetch, 10000).catch(() => ({ transactions: [], count: 0 }))  // 10000 transações para exibir histórico completo
      ])

      setGateway(gatewayData)
      setBalance(balanceData)
      setTransactions(transactionsData.transactions || [])
    } catch (error: any) {
      console.error('Erro ao carregar dados do gateway:', error)
      alert('Erro ao carregar dados: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    try {
      setSyncing(true)
      await paymentGatewayAPI.syncGateway(id!)
      await loadGatewayData()
      alert('Sincronização concluída com sucesso!')
    } catch (error: any) {
      console.error('Erro ao sincronizar:', error)
      alert('Erro ao sincronizar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSyncing(false)
    }
  }

  const handleFullHistorySync = async () => {
    const confirmed = window.confirm(
      'Sincronizar Histórico Completo irá buscar os últimos 12 meses de dados. Isso pode levar alguns minutos. Deseja continuar?'
    )

    if (!confirmed) return

    try {
      setSyncingFullHistory(true)
      const result = await paymentGatewayAPI.syncGateway(id!, 'all', true)
      await loadGatewayData(365)  // Buscar 365 dias (12 meses) de histórico

      const message = result.is_full_sync
        ? `Sincronização histórica concluída!\n\n✅ ${result.transactions_synced} transações sincronizadas dos últimos ${result.period_days} dias`
        : `Sincronização concluída!\n\n✅ ${result.transactions_synced} transações sincronizadas`

      alert(message)
    } catch (error: any) {
      console.error('Erro ao sincronizar histórico completo:', error)
      alert('Erro ao sincronizar histórico: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSyncingFullHistory(false)
    }
  }

  const handleSyncToBank = async () => {
    console.log('🚀 handleSyncToBank INICIADO - Gateway ID:', id)
    try {
      setSyncingToBank(true)
      console.log('📡 Chamando paymentGatewayAPI.syncGatewayToBank com ID:', id)
      const result = await paymentGatewayAPI.syncGatewayToBank(id!)
      console.log('✅ Resposta recebida:', result)

      // Mensagem melhorada com mais detalhes
      const details = [
        `Conta Bancária ID: ${result.bank_account_id}`,
        `Transações no período: ${result.total_transactions_in_period || result.transactions_synced}`,
        `Novas transações criadas: ${result.transactions_synced}`,
        `Contas a pagar/receber criadas: ${result.bills_created || 0}`,
        `Saldo atual: R$ ${result.current_balance.toFixed(2)}`
      ].join('\n')

      alert(`${result.message}\n\n${details}`)
      await loadGatewayData()
    } catch (error: any) {
      console.error('❌ Erro ao sincronizar com banco:', error)
      console.error('❌ Error details:', error.response?.data)
      alert('Erro ao sincronizar com banco: ' + (error.response?.data?.detail || error.message))
    } finally {
      console.log('🏁 handleSyncToBank FINALIZADO')
      setSyncingToBank(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'RECEIVED':
      case 'PAYMENT_RECEIVED':
        return <ArrowDownIcon className="w-5 h-5 text-green-600" />
      case 'PAYMENT':
      case 'TRANSFER_OUT':
        return <ArrowUpIcon className="w-5 h-5 text-red-600" />
      default:
        return <CreditCardIcon className="w-5 h-5 text-gray-600" />
    }
  }

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'RECEIVED':
      case 'PAYMENT_RECEIVED':
        return 'text-green-600'
      case 'PAYMENT':
      case 'TRANSFER_OUT':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  // Cálculo de paginação
  const totalPages = Math.ceil(transactions.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTransactions = transactions.slice(startIndex, endIndex)

  // Helper para calcular dias desde o início do mês
  const getDaysFromStartOfMonth = () => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const diffTime = Math.abs(now.getTime() - startOfMonth.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  // Helper para calcular dias do mês passado
  const getDaysFromLastMonth = () => {
    const now = new Date()
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const diffTime = Math.abs(now.getTime() - startOfLastMonth.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // Handler para mudar filtro de período
  const handlePeriodChange = (days: number, label: string) => {
    setPeriodFilter(days)
    setPeriodLabel(label)
    setShowPeriodDropdown(false)
    setShowCustomDatePicker(false)
    setCurrentPage(1)
  }

  // Handler para período personalizado
  const handleCustomPeriod = () => {
    if (!customStartDate || !customEndDate) {
      alert('Por favor, selecione as datas de início e fim')
      return
    }

    const start = new Date(customStartDate)
    const end = new Date(customEndDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    setPeriodFilter(diffDays)
    setPeriodLabel(`${start.toLocaleDateString('pt-BR')} - ${end.toLocaleDateString('pt-BR')}`)
    setShowPeriodDropdown(false)
    setShowCustomDatePicker(false)
    setCurrentPage(1)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    )
  }

  if (!gateway) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Gateway não encontrado</h2>
          <button
            onClick={() => navigate('/integrations')}
            className="text-green-600 hover:text-green-700"
          >
            Voltar para Integrações
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/integrations')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{gateway.name || 'Pagar.me'}</h1>
                <p className="text-sm text-gray-500">Gateway de Pagamento • {gateway.environment} • API v5</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {gateway.is_active ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                  <CheckCircleIcon className="w-4 h-4" />
                  Ativo
                </span>
              ) : (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                  <ExclamationCircleIcon className="w-4 h-4" />
                  Inativo
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            {/* Criar Pedidos - Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                <PlusCircleIcon className="w-5 h-5" />
                Criar Pedido
                <ChevronDownIcon className="w-4 h-4" />
              </button>

              {showCreateMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCreateMenu(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                    <button
                      onClick={() => {
                        setShowPixModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <QrCodeIcon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">PIX</p>
                        <p className="text-xs text-gray-500">Pagamento instantâneo</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowCardModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCardIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Cartão de Crédito</p>
                        <p className="text-xs text-gray-500">Crédito e parcelamento</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowBoletoModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Boleto</p>
                        <p className="text-xs text-gray-500">Pagamento bancário</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Botões de Sincronização */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSync}
                disabled={syncing || syncingFullHistory}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium"
              >
                <ArrowPathIcon className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
              <button
                onClick={handleFullHistorySync}
                disabled={syncing || syncingFullHistory}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium"
                title="Sincronizar últimos 12 meses de dados"
              >
                <ClockIcon className={`w-5 h-5 ${syncingFullHistory ? 'animate-spin' : ''}`} />
                {syncingFullHistory ? 'Histórico...' : 'Histórico 12m'}
              </button>
              <button
                onClick={handleSyncToBank}
                disabled={syncingToBank}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium"
              >
                <BanknotesIcon className={`w-5 h-5 ${syncingToBank ? 'animate-pulse' : ''}`} />
                {syncingToBank ? 'Sincronizando...' : 'Sync Banco'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PIX Order Modal */}
      <CreatePixOrderModal
        isOpen={showPixModal}
        onClose={() => setShowPixModal(false)}
        gatewayId={id!}
        onSuccess={() => {
          // Recarregar dados após criar pedido
          loadGatewayData()
        }}
      />

      {/* Credit Card Order Modal */}
      <CreateCreditCardOrderModal
        isOpen={showCardModal}
        onClose={() => setShowCardModal(false)}
        gatewayId={id!}
        onSuccess={() => {
          // Recarregar dados após criar pedido
          loadGatewayData()
        }}
      />

      {/* Boleto Order Modal */}
      <CreateBoletoOrderModal
        isOpen={showBoletoModal}
        onClose={() => setShowBoletoModal(false)}
        gatewayId={id!}
        onSuccess={() => {
          // Recarregar dados após criar pedido
          loadGatewayData()
        }}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Disponível</span>
            <BanknotesIcon className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.available_balance || balance.balance) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Disponível para saque</p>
        </div>

        {balance?.waiting_funds > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">A Receber</span>
              <ClockIcon className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(balance.waiting_funds)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Saldo a liberar</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Saldo Total</span>
            <CreditCardIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.balance) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Saldo na conta Pagar.me</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Transações</span>
            <ArrowPathIcon className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {transactions.length}
          </p>
          <p className="text-xs text-gray-500 mt-1">Exibindo histórico</p>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Transações</h2>

            {/* Filtro de Período - Dropdown Moderno */}
            <div className="relative period-filter-dropdown">
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <CalendarIcon className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{periodLabel}</span>
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              </button>

              {/* Dropdown Menu */}
              {showPeriodDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                  <div className="py-1">
                    {/* Opções Predefinidas */}
                    <button
                      onClick={() => handlePeriodChange(15, 'Últimos 15 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 15 dias</span>
                      {periodFilter === 15 && periodLabel === 'Últimos 15 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(30, 'Últimos 30 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 30 dias</span>
                      {periodFilter === 30 && periodLabel === 'Últimos 30 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(60, 'Últimos 60 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 60 dias</span>
                      {periodFilter === 60 && periodLabel === 'Últimos 60 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(getDaysFromStartOfMonth(), 'Mês atual')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Mês atual</span>
                      {periodLabel === 'Mês atual' && (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(getDaysFromLastMonth(), 'Mês passado')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Mês passado</span>
                      {periodLabel === 'Mês passado' && (
                        <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      )}
                    </button>

                    {/* Divisor */}
                    <div className="border-t border-gray-200 my-1"></div>

                    {/* Período Personalizado */}
                    <button
                      onClick={() => {
                        setShowCustomDatePicker(!showCustomDatePicker)
                        setShowPeriodDropdown(false)
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-green-600 hover:bg-green-50 transition-colors"
                    >
                      📅 Selecionar período
                    </button>
                  </div>
                </div>
              )}

              {/* Date Picker Personalizado */}
              {showCustomDatePicker && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Período Personalizado</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Data Início</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Data Fim</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleCustomPeriod}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                      >
                        Aplicar
                      </button>
                      <button
                        onClick={() => setShowCustomDatePicker(false)}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {transactions.length === 0 ? (
            <div className="p-12 text-center">
              <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Nenhuma transação nos últimos {periodFilter} dias</p>
              <p className="text-sm text-gray-400 mt-2">Clique em "Sincronizar" para buscar dados do Pagar.me</p>
            </div>
          ) : (
            <>
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{transaction.description}</div>
                      {transaction.customer && (
                        <div className="text-xs text-gray-500">
                          {typeof transaction.customer === 'string'
                            ? transaction.customer
                            : transaction.customer.name || transaction.customer.email || 'Cliente'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(transaction.type)}
                        <span className="text-sm text-gray-700">{transaction.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        transaction.status === 'RECEIVED' || transaction.status === 'CONFIRMED'
                          ? 'bg-green-100 text-green-800'
                          : transaction.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold text-right ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'RECEIVED' || transaction.type === 'PAYMENT_RECEIVED' ? '+' : '-'}
                      {formatCurrency(Math.abs(transaction.amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Exibindo <span className="font-medium">{startIndex + 1}</span> a{' '}
                  <span className="font-medium">{Math.min(endIndex, transactions.length)}</span> de{' '}
                  <span className="font-medium">{transactions.length}</span> transações
                </div>

                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Anterior
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)

                      if (!showPage) {
                        // Show ellipsis
                        if (page === 2 && currentPage > 3) {
                          return <span key={page} className="px-2 text-gray-500">...</span>
                        }
                        if (page === totalPages - 1 && currentPage < totalPages - 2) {
                          return <span key={page} className="px-2 text-gray-500">...</span>
                        }
                        return null
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            currentPage === page
                              ? 'bg-green-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </div>

      {/* Last Sync Info */}
      {gateway.last_sync && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-green-800">
            <CheckCircleIcon className="w-5 h-5" />
            <span>Última sincronização: {formatDate(gateway.last_sync)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default PagarmeDashboard
